import type { AppointmentResultReportCondition } from '@/api/types/ProviderApi'
import { ScoreArc } from '@/components/CarePage/ScoreArc'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import {
	NOTHING_FOUND,
	NOTHING_FOUND_LINE,
	SCORE_EXPLAINED,
	SCORE_OPENER,
	type VisitRecord,
	visitSubtitle,
} from '@/features/care/visits'
import { Swivel } from '@/ui/core/motion'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { type DimensionValue, View } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'

/**
 * The card the score sits on, and the air around it.
 *
 * `surface` is the grey the Care chart's own panel uses, so the two places this app draws a score
 * are mounted the same way. There is no rule under the arc: a divider cut a small card in two, and
 * the gap does the separating instead.
 */
const MOUNT = 20
const OPENER_GAP = 22

const ScoreCardPanel = ({
	score,
	ghost = false,
	children,
}: { score?: number; ghost?: boolean; children?: React.ReactNode }) => (
	<View
		className="items-center rounded-[20px] bg-surface"
		style={{ padding: MOUNT, paddingBottom: children ? MOUNT - 4 : MOUNT }}
	>
		<ScoreArc score={score} ghost={ghost} />
		{children}
	</View>
)

/**
 * What the number is, behind a tap.
 *
 * Centred rather than set as a row: the chevron comes to the words, so the label reads as one
 * object you press instead of a strip with a mark at the far end of it. It opens in place — the
 * gesture the visit rows use — because the alternative is a second sheet over a sheet, and because
 * nothing above it moves when it opens, so the figure stays where the eye left it.
 */
const HowItWorks = () => {
	const [open, setOpen] = useState(false)

	return (
		<>
			<Pressable
				onPress={() => setOpen((was) => !was)}
				accessibilityRole="button"
				accessibilityState={{ expanded: open }}
				accessibilityLabel={`${SCORE_OPENER}. ${open ? 'Hide' : 'Show'}`}
				className="flex-row items-center"
				style={{ marginTop: OPENER_GAP, gap: 7 }}
			>
				<Text size="body1" weight="bold">
					{SCORE_OPENER}
				</Text>
				<Swivel deg={open ? -90 : 0}>
					<Glyph name="chevron" size={12} color={colors.softInk} />
				</Swivel>
			</Pressable>
			{open && (
				<Animated.View entering={FadeIn.duration(200)} style={{ marginTop: 14 }}>
					<Text size="body2" tone="soft" className="text-center">
						{SCORE_EXPLAINED}
					</Text>
				</Animated.View>
			)}
		</>
	)
}

/** One finding: its number on a disc, the clinician's title, and their words beneath. */
const Finding = ({
	condition,
	index,
	last,
}: { condition: AppointmentResultReportCondition; index: number; last: boolean }) => (
	<View className="relative">
		{!last && (
			<View
				className="absolute right-0 bottom-0 h-px"
				style={{ left: 54, backgroundColor: colors.divider }}
			/>
		)}
		<HStack gap={14} className="items-start py-4">
			{/* Blush rather than the white these discs take on a card: the sheet is white, so a
			    white disc on it is not a disc. */}
			<View className="h-10 w-10 items-center justify-center rounded-full bg-blush">
				<Text className="font-display text-[17px] leading-[20px] tracking-[-0.02em]">
					{String(index + 1).padStart(2, '0')}
				</Text>
			</View>
			<VStack gap={5} className="flex-1 pt-1.5">
				<Text size="body0" weight="bold">
					{condition.title}
				</Text>
				<Text tone="soft">{condition.description}</Text>
			</VStack>
		</HStack>
	</View>
)

/** A line where the list would be, for a visit that found nothing and one we cannot read. */
const Line = ({
	glyph,
	title,
	line,
}: { glyph: 'check' | 'info'; title?: string; line: string }) => (
	<HStack gap={12} className="items-center pt-4">
		<View className="h-[34px] w-[34px] items-center justify-center rounded-full bg-blush">
			<Glyph name={glyph} size={15} color={colors.ink} />
		</View>
		<VStack gap={2} className="flex-1">
			{!!title && (
				<Text size="body1" weight="bold">
					{title}
				</Text>
			)}
			<Text size="body2" tone="soft">
				{line}
			</Text>
		</VStack>
	</HStack>
)

/**
 * The caption over the findings.
 *
 * Twenty-eight above it rather than the seventeen a `pt-5` gave: the card below the arc already
 * ends in its own padding, so a small gap put the caption nearer the card than the list it labels
 * and it read as the card's footer.
 */
const Caption = ({ children }: { children: string }) => (
	<Text
		size="body3"
		tone="muted"
		className="tracking-[0.08em] uppercase"
		style={{ paddingTop: 28 }}
	>
		{children}
	</Text>
)

/** The bars a loading sheet is drawn with, in the tone the Care chart's empty slots use. */
const GhostBar = ({ width, height = 13 }: { width: DimensionValue; height?: number }) => (
	<View
		style={{ width, height, borderRadius: 7, backgroundColor: 'rgba(125,111,111,0.13)' }}
	/>
)

const GhostRows = () => (
	<VStack gap={0} style={{ marginTop: 22 }}>
		<GhostBar width="44%" height={10} />
		{[0, 1].map((row) => (
			<HStack key={row} gap={14} className="items-start py-4">
				<View
					className="h-10 w-10 rounded-full"
					style={{ backgroundColor: 'rgba(125,111,111,0.13)' }}
				/>
				<VStack gap={8} className="flex-1 pt-1.5">
					<GhostBar width={row ? '52%' : '68%'} />
					<GhostBar width="92%" height={10} />
					<GhostBar width={row ? '40%' : '74%'} height={10} />
				</VStack>
			</HStack>
		))}
	</VStack>
)

/**
 * A visit's results, over the visit's own page.
 *
 * A sheet rather than a route, and drawn from the visit it was opened on. The row used to push
 * `/score`, which renders whatever result is newest whatever visit you came from — so a member
 * opening March read September's findings. There is no route left here to be wrong about which
 * visit was meant.
 *
 * Two states, because a score and its findings are released together: a report with findings (the
 * mean is 2.68) and a report with none (the minimum is nought). There is no state for a score
 * whose findings are missing — a visit like that does not open at all, since a sheet that can only
 * say "not here yet" is a door onto an empty room.
 */
export const ResultsSheet = ({
	visit,
	open,
	onClose,
	isLoading = false,
}: {
	visit: VisitRecord
	open: boolean
	onClose: () => void
	isLoading?: boolean
}) => {
	const results = visit.results
	const conditions = results?.conditions
	const count = conditions?.length ?? 0

	return (
		<Sheet open={open} onClose={onClose}>
			{isLoading ? (
				<VStack gap={0} className="pt-2 pb-2">
					<HStack gap={12} className="items-center px-4 mt-4 mb-4">
						<View
							className="h-10 w-10 rounded-full"
							style={{ backgroundColor: 'rgba(125,111,111,0.13)' }}
						/>
						<VStack gap={7} className="flex-1">
							<GhostBar width="38%" height={15} />
							<GhostBar width="56%" height={11} />
						</VStack>
					</HStack>
					<View className="px-4">
						<ScoreCardPanel ghost />
						<GhostRows />
					</View>
				</VStack>
			) : (
				<VStack gap={0} className="pt-2 pb-2">
					{/* The same mark as the row that opened it, so the sheet is visibly that row
					    grown rather than a new subject. */}
					<SheetTitle glyph="gauge" line={visitSubtitle(visit)}>
						Results
					</SheetTitle>
					{/* The title owns the air below it, so the card takes the sheet's own inset and
					    nothing else. */}
					<View className="px-4">
						<ScoreCardPanel score={results?.score}>
							<HowItWorks />
						</ScoreCardPanel>
						{count === 0 ? (
							// No caption: an empty list under "What we found" reads as a failure to
							// load, and nothing found is good news rather than an absence.
							<Line glyph="check" title={NOTHING_FOUND} line={NOTHING_FOUND_LINE} />
						) : (
							<>
								{/* At ten findings the words understate what is under them, so past
								    four the caption counts instead. */}
								<Caption>{count > 4 ? `${count} findings` : 'What we found'}</Caption>
								{conditions?.map((condition, index) => (
									<Finding
										key={condition.title}
										condition={condition}
										index={index}
										last={index === count - 1}
									/>
								))}
							</>
						)}
					</View>
				</VStack>
			)}
		</Sheet>
	)
}
